import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/App";
import { authAxios } from "../utils/auth";
import DOMPurify from "dompurify";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, RefreshCw, Heart, Users, Sparkles, TrendingUp, Star, AlertTriangle, Lightbulb, Wand2, User, Menu, X, FileText, Brain, Home, Gem, Settings, Compass } from "lucide-react";
import { toast } from "sonner";
import { getConnectionType, getConnectionColor } from "@/utils/connectionTypes";
import AdminMenu from "../components/AdminMenu";
import { useCoinGate } from "../contexts/CoinGateContext";

const CompatibilityReport = () => {
  const navigate = useNavigate();
  const { matchId } = useParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const { requireCoins } = useCoinGate();
  const [rewriting, setRewriting] = useState(false);
  const [match, setMatch] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [currentTheme, setCurrentTheme] = useState("analytical");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [userPersona, setUserPersona] = useState(null);

  // Get score circle color based on compatibility score
  const getScoreCircleColor = (score) => {
    if (score >= 92) return { border: 'border-pink-400', text: 'text-pink-400', bg: 'bg-pink-400/20', glow: 'shadow-pink-400/50' };
    if (score >= 84) return { border: 'border-fuchsia-500', text: 'text-fuchsia-500', bg: 'bg-fuchsia-500/20', glow: 'shadow-fuchsia-500/50' };
    if (score >= 75) return { border: 'border-yellow-400', text: 'text-yellow-400', bg: 'bg-yellow-400/20', glow: 'shadow-yellow-400/50' };
    if (score >= 66) return { border: 'border-red-500', text: 'text-red-500', bg: 'bg-red-500/20', glow: 'shadow-red-500/50' };
    if (score >= 51) return { border: 'border-green-500', text: 'text-green-500', bg: 'bg-green-500/20', glow: 'shadow-green-500/50' };
    return { border: 'border-gray-400', text: 'text-gray-400', bg: 'bg-gray-400/20', glow: 'shadow-gray-400/50' };
  };

  const tabItems = [
    { value: "overview", label: "Overview", icon: FileText },
    { value: "personality", label: "Personality", icon: Brain },
    { value: "values", label: "Values", icon: Home },
    { value: "physical", label: "Physical", icon: Sparkles },
    { value: "romantic", label: "Romantic", icon: Heart },
    { value: "practical", label: "Practical", icon: Settings },
    { value: "astro", label: "Astrology", icon: Compass },
    { value: "strengths", label: "Strengths", icon: TrendingUp },
    { value: "challenges", label: "Tips", icon: Lightbulb },
  ];

  useEffect(() => {
    fetchMatch();
    fetchUserPersona();
  }, [matchId]);

  const fetchUserPersona = async () => {
    try {
      const response = await authAxios.get("/matches/user-persona");
      setUserPersona(response.data);
    } catch (error) {
      console.log("No user persona found");
    }
  };

  const fetchMatch = async () => {
    try {
      const response = await authAxios.get(`/matches/${matchId}`);
      setMatch(response.data);
      
      // Handle structured analysis data
      const analysisData = response.data.analysis;
      if (analysisData) {
        setAnalysis({
          ...analysisData,
          compatibility_score: analysisData.compatibility_score,
          summary: analysisData.summary || analysisData.structured_data?.executive_summary,
          structured_data: analysisData.structured_data,
          zodiac_compatibility: analysisData.zodiac_compatibility
        });
        setCurrentTheme(analysisData.report_theme || "analytical");
      } else {
        setAnalysis(null);
      }
      
      setLoading(false);
    } catch (error) {
      console.error("Error fetching match:", error);
      toast.error("Relationship not found");
      navigate("/relationships");
    }
  };

  const handleAnalyze = async () => {
    if (!(await requireCoins())) return;
    setAnalyzing(true);
    try {
      const response = await authAxios.post(`/ai/analyze-compatibility?match_id=${matchId}`, {});
      // Store structured data from response
      setAnalysis({
        compatibility_score: response.data.compatibility_score,
        summary: response.data.structured_data?.executive_summary || "",
        structured_data: response.data.structured_data,
        zodiac_compatibility: response.data.zodiac_compatibility,
        silo_enhanced: response.data.silo_enhanced,
        llm_used: response.data.llm_used
      });
      setCurrentTheme("analytical");
      toast.success("Compatibility analysis complete!");
      fetchMatch(); // Refresh match data
    } catch (error) {
      console.error("Error analyzing compatibility:", error);
      toast.error(error.response?.data?.detail || "Failed to analyze compatibility");
    } finally {
      setAnalyzing(false);
    }
  };

  const handleRewriteTheme = async (theme) => {
    setRewriting(true);
    setAnalyzing(true);  // Show re-analyze animation
    
    try {
      const response = await authAxios.post(
        `/ai/rewrite-compatibility/${matchId}?theme=${theme}`,
        {}
      );
      
      toast.success(`Report rewritten in ${theme} theme!`);
      
      // Refresh the page to show updated content
      window.location.reload();
      
    } catch (error) {
      console.error("Error rewriting:", error);
      toast.error("Failed to rewrite report");
      setRewriting(false);
      setAnalyzing(false);
    }
  };

  // Helper to safely render HTML content
  const renderHTML = (htmlContent) => {
    if (!htmlContent) return null;
    return <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(htmlContent) }} />;
  };

  // Helper to render list items
  const renderList = (items, icon) => {
    if (!items || !Array.isArray(items) || items.length === 0) return <p className="text-white/60">No data available</p>;
    return (
      <ul className="space-y-3">
        {items.map((item, idx) => (
          <li key={idx} className="flex items-start gap-3">
            {icon && <span className="text-divine-gold mt-1">{icon}</span>}
            <span className="text-white/90">{item}</span>
          </li>
        ))}
      </ul>
    );
  };

  // Get structured data or fallback to parsed sections
  const structuredData = analysis?.structured_data || {};
  
  // Fallback: Parse markdown sections if no structured data (for backwards compatibility)
  const parseReportSections = (fullReport) => {
    if (!fullReport) return {};
    
    const sections = {};
    const parts = fullReport.split(/(?=##\s*\d*\)?\.?\s*)/);
    
    parts.forEach(part => {
      const trimmed = part.trim();
      if (!trimmed || !trimmed.startsWith('##')) return;
      
      const lines = trimmed.split('\n');
      if (lines.length === 0) return;
      
      let header = lines[0]
        .replace(/^##\s*/, '')
        .replace(/^\d+\)?\s*\.?\s*/, '')
        .replace(/\s*\([^)]*\)\s*$/, '')
        .trim()
        .toUpperCase();
      
      const keyMap = {
        'COMPATIBILITY SCORE': 'COMPATIBILITY SCORE',
        'EXECUTIVE SUMMARY': 'EXECUTIVE SUMMARY',
        'PERSONALITY MATCH': 'PERSONALITY MATCH',
        'VALUES & LIFESTYLE': 'VALUES & LIFESTYLE',
        'VALUES AND LIFESTYLE': 'VALUES & LIFESTYLE',
        'ROMANTIC COMPATIBILITY': 'ROMANTIC COMPATIBILITY',
        'PRACTICAL ALIGNMENT': 'PRACTICAL ALIGNMENT',
        'ASTROLOGICAL INSIGHTS': 'ASTROLOGICAL INSIGHTS',
        'STRENGTHS': 'STRENGTHS',
        'CHALLENGES': 'CHALLENGES',
        'RECOMMENDATIONS': 'RECOMMENDATIONS',
      };
      
      const normalizedKey = Object.keys(keyMap).find(k => header.includes(k));
      const key = normalizedKey ? keyMap[normalizedKey] : header;
      
      const content = lines.slice(1).join('\n').trim();
      if (content) {
        sections[key] = content;
      }
    });
    
    return sections;
  };

  const legacySections = analysis?.full_report ? parseReportSections(analysis.full_report) : {};

  if (loading) {
    return (
      <div className="min-h-screen bg-divine-black flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-divine-gold border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-divine-black">
      {/* Header */}
      <header className="border-b border-white/10 bg-divine-black-light/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                onClick={() => (window.history.length > 1 ? navigate(-1) : navigate(`/relationships/${matchId}`))}
                className="text-white/60 hover:text-white"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              
              {/* Mobile: Match Avatar - Links to match profile */}
              <div 
                className="md:hidden w-10 h-10 rounded-full bg-divine-gold/20 flex items-center justify-center overflow-hidden border-2 border-divine-gold/30 flex-shrink-0 cursor-pointer hover:border-divine-gold/50 transition-colors"
                onClick={() => navigate(`/relationships/${matchId}`)}
                data-testid="header-match-avatar"
              >
                {(match?.thumbnail_url || match?.picture_url || match?.picture) ? (
                  <img 
                    src={match.thumbnail_url || match.picture_url || match.picture} 
                    alt={match?.name} 
                    className="w-full h-full object-cover" 
                  />
                ) : (
                  <span className="text-divine-gold font-cinzel text-sm">{match?.name?.[0]}</span>
                )}
              </div>
              
              <div>
                <h1 className="font-cinzel text-xl text-white">
                  <span className="hidden md:inline">Compatibility Report: </span>{match?.name}
                </h1>
                <p className="text-white/40 text-sm hidden md:block">Comprehensive relationship analysis</p>
              </div>
            </div>
            
            {/* Desktop Controls */}
            <div className="hidden md:flex items-center gap-3">
              <Button
                onClick={handleAnalyze}
                disabled={analyzing}
                className="bg-divine-gold hover:bg-divine-gold-light text-black font-cinzel"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${analyzing ? 'animate-spin' : ''}`} />
                {analyzing ? "Analyzing..." : "Re-Analyze"}
              </Button>
            </div>
            
            {/* Mobile Menu Button - In Header */}
            <div className="flex items-center gap-1">
              <AdminMenu />
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden text-white hover:bg-white/10"
                data-testid="compatibility-mobile-menu-btn"
              >
                {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
            </div>
          </div>
          
          {/* Mobile Dropdown Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden absolute left-0 right-0 top-full bg-divine-black-light border-b border-white/10 shadow-lg z-50">
              {/* Home link — takes user out of the compatibility flow */}
              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  navigate("/dashboard");
                }}
                className="w-full flex items-center gap-3 px-6 py-3 text-left border-b border-white/10 text-white/70 hover:bg-white/5 hover:text-white transition-colors"
                data-testid="mobile-menu-home"
              >
                <Home className="h-4 w-4" />
                <span className="text-sm">Home</span>
              </button>

              {/* Tab Items */}
              <div className="py-2">
                {tabItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.value}
                      onClick={() => {
                        setActiveTab(item.value);
                        setMobileMenuOpen(false);
                      }}
                      className={`w-full flex items-center gap-3 px-6 py-3 text-left transition-colors ${
                        activeTab === item.value
                          ? "bg-divine-gold/20 text-divine-gold"
                          : "text-white/70 hover:bg-white/5 hover:text-white"
                      }`}
                      data-testid={`mobile-tab-${item.value}`}
                    >
                      <Icon className="h-4 w-4" />
                      <span className="text-sm">{item.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Mobile Controls - Below Header */}
      <div className="md:hidden px-6 py-4 border-b border-white/10 space-y-3">
        <Button
          onClick={handleAnalyze}
          disabled={analyzing}
          className="w-full bg-divine-gold hover:bg-divine-gold-light text-black font-cinzel"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${analyzing ? 'animate-spin' : ''}`} />
          {analyzing ? "Analyzing..." : "Re-Analyze"}
        </Button>
      </div>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-8">
        {!analysis ? (
          <Card className="bg-divine-charcoal border-divine-gold/20">
            <CardContent className="py-16 text-center">
              <Heart className="w-16 h-16 text-white/20 mx-auto mb-4" />
              <h3 className="font-cinzel text-xl text-white mb-3">No Analysis Yet</h3>
              <p className="text-white/60 mb-6">Generate a comprehensive compatibility analysis</p>
              <Button
                onClick={handleAnalyze}
                disabled={analyzing}
                className="bg-divine-gold hover:bg-divine-gold-light text-black font-cinzel"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                {analyzing ? "Analyzing..." : "Analyze Compatibility"}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Score Card */}
            <Card className="bg-divine-charcoal border-divine-gold/20">
              <CardContent className="py-8">
                {/* Desktop: Horizontal Layout */}
                <div className="hidden md:flex items-center justify-between gap-8 max-w-4xl mx-auto">
                  {/* User Persona Avatar - Left */}
                  <div className="text-center flex-shrink-0">
                    <div className="w-32 h-32 rounded-full bg-divine-gold/20 flex items-center justify-center mx-auto mb-3 overflow-hidden border-4 border-divine-gold/30">
                      {(userPersona?.thumbnail_url || userPersona?.picture_url || userPersona?.picture) ? (
                        <img src={userPersona.thumbnail_url || userPersona.picture_url || userPersona.picture} alt={userPersona.name} className="w-full h-full object-cover" />
                      ) : user?.picture ? (
                        <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
                      ) : (
                        <User className="w-16 h-16 text-divine-gold" />
                      )}
                    </div>
                    <p className="text-white/80 text-sm font-medium">{userPersona?.name || user?.name}</p>
                  </div>
                  
                  {/* Score Circle - Center */}
                  <div className="text-center flex-1">
                    <div className="relative inline-block mb-4">
                      {(() => {
                        const scoreColors = getScoreCircleColor(analysis.compatibility_score);
                        const shouldPulse = analysis.compatibility_score >= 90;
                        return (
                          <div 
                            className={`w-32 h-32 rounded-full border-8 ${scoreColors.border} flex items-center justify-center ${
                              shouldPulse ? 'animate-pulse shadow-lg' : ''
                            } ${shouldPulse ? scoreColors.glow : ''}`}
                            style={shouldPulse ? { 
                              animationDuration: '1.5s',
                              boxShadow: `0 0 20px 5px ${analysis.compatibility_score >= 92 ? 'rgba(244,114,182,0.4)' : 'rgba(217,70,239,0.4)'}`
                            } : {}}
                          >
                            <span className={`text-5xl font-bold ${scoreColors.text}`}>
                              {analysis.compatibility_score}<span className="text-2xl">%</span>
                            </span>
                          </div>
                        );
                      })()}
                    </div>
                    <h2 className={`font-cinzel text-3xl mb-2 ${getConnectionColor(analysis.compatibility_score)}`}>
                      {getConnectionType(analysis.compatibility_score)}
                    </h2>
                    <p className="text-white/60 text-sm">COMPATIBILITY RANKING</p>
                  </div>
                  
                  {/* Match Avatar - Right */}
                  <div className="text-center flex-shrink-0">
                    <div className="w-32 h-32 rounded-full bg-divine-gold/20 flex items-center justify-center mx-auto mb-3 overflow-hidden border-4 border-divine-gold/30">
                      {(match?.picture_url || match?.picture) ? (
                        <img src={match.picture_url || match.picture} alt={match.name} className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-divine-gold font-cinzel text-6xl">{match?.name?.[0]}</span>
                      )}
                    </div>
                    <p className="text-white/80 text-sm font-medium">{match?.name}</p>
                  </div>
                </div>
                
                {/* Mobile: Vertical Layout */}
                <div className="md:hidden flex flex-col items-center gap-6">
                  {/* User Persona Avatar - Top */}
                  <div className="text-center">
                    <div className="w-24 h-24 rounded-full bg-divine-gold/20 flex items-center justify-center mx-auto mb-2 overflow-hidden border-4 border-divine-gold/30">
                      {(userPersona?.thumbnail_url || userPersona?.picture_url || userPersona?.picture) ? (
                        <img src={userPersona.thumbnail_url || userPersona.picture_url || userPersona.picture} alt={userPersona.name} className="w-full h-full object-cover" />
                      ) : user?.picture ? (
                        <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
                      ) : (
                        <User className="w-12 h-12 text-divine-gold" />
                      )}
                    </div>
                    <p className="text-white/80 text-sm font-medium">{userPersona?.name || user?.name}</p>
                  </div>
                  
                  {/* Score Circle - Center */}
                  <div className="text-center">
                    <div className="relative inline-block mb-3">
                      {(() => {
                        const scoreColors = getScoreCircleColor(analysis.compatibility_score);
                        const shouldPulse = analysis.compatibility_score >= 90;
                        return (
                          <div 
                            className={`w-28 h-28 rounded-full border-8 ${scoreColors.border} flex items-center justify-center ${
                              shouldPulse ? 'animate-pulse shadow-lg' : ''
                            } ${shouldPulse ? scoreColors.glow : ''}`}
                            style={shouldPulse ? { 
                              animationDuration: '1.5s',
                              boxShadow: `0 0 20px 5px ${analysis.compatibility_score >= 92 ? 'rgba(244,114,182,0.4)' : 'rgba(217,70,239,0.4)'}`
                            } : {}}
                          >
                            <span className={`text-4xl font-bold ${scoreColors.text}`}>
                              {analysis.compatibility_score}<span className="text-xl">%</span>
                            </span>
                          </div>
                        );
                      })()}
                    </div>
                    <h2 className={`font-cinzel text-2xl mb-1 ${getConnectionColor(analysis.compatibility_score)}`}>
                      {getConnectionType(analysis.compatibility_score)}
                    </h2>
                    <p className="text-white/60 text-xs">COMPATIBILITY RANKING</p>
                  </div>
                  
                  {/* Match Avatar - Bottom */}
                  <div className="text-center">
                    <div className="w-24 h-24 rounded-full bg-divine-gold/20 flex items-center justify-center mx-auto mb-2 overflow-hidden border-4 border-divine-gold/30">
                      {(match?.picture_url || match?.picture) ? (
                        <img src={match.picture_url || match.picture} alt={match.name} className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-divine-gold font-cinzel text-4xl">{match?.name?.[0]}</span>
                      )}
                    </div>
                    <p className="text-white/80 text-sm font-medium">{match?.name}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="hidden md:grid w-full grid-cols-4 lg:grid-cols-9 bg-divine-charcoal border border-divine-gold/20">
                <TabsTrigger value="overview" className="data-[state=active]:bg-divine-gold data-[state=active]:text-black">Overview</TabsTrigger>
                <TabsTrigger value="personality" className="data-[state=active]:bg-divine-gold data-[state=active]:text-black">Personality</TabsTrigger>
                <TabsTrigger value="values" className="data-[state=active]:bg-divine-gold data-[state=active]:text-black">Values</TabsTrigger>
                <TabsTrigger value="physical" className="data-[state=active]:bg-divine-gold data-[state=active]:text-black">Physical</TabsTrigger>
                <TabsTrigger value="romantic" className="data-[state=active]:bg-divine-gold data-[state=active]:text-black">Romantic</TabsTrigger>
                <TabsTrigger value="practical" className="data-[state=active]:bg-divine-gold data-[state=active]:text-black">Practical</TabsTrigger>
                <TabsTrigger value="astro" className="data-[state=active]:bg-divine-gold data-[state=active]:text-black">Astrology</TabsTrigger>
                <TabsTrigger value="strengths" className="data-[state=active]:bg-divine-gold data-[state=active]:text-black">Strengths</TabsTrigger>
                <TabsTrigger value="challenges" className="data-[state=active]:bg-divine-gold data-[state=active]:text-black">Tips</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="mt-6">
                <Card className="bg-divine-charcoal border-divine-gold/20">
                  <CardHeader>
                    <CardTitle className="text-divine-gold font-cinzel">Executive Summary</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="prose prose-invert max-w-none text-white/90 leading-relaxed">
                      {structuredData.executive_summary 
                        ? renderHTML(structuredData.executive_summary)
                        : <p className="whitespace-pre-wrap">{analysis.summary || legacySections['EXECUTIVE SUMMARY'] || 'No summary available'}</p>
                      }
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="personality" className="mt-6">
                <Card className="bg-divine-charcoal border-divine-gold/20">
                  <CardHeader>
                    <CardTitle className="text-divine-gold font-cinzel">Personality Match</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="prose prose-invert max-w-none text-white/90 leading-relaxed">
                      {structuredData.personality_match 
                        ? renderHTML(structuredData.personality_match)
                        : <p className="whitespace-pre-wrap">{legacySections['PERSONALITY MATCH'] || 'Analysis not available'}</p>
                      }
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="values" className="mt-6">
                <Card className="bg-divine-charcoal border-divine-gold/20">
                  <CardHeader>
                    <CardTitle className="text-divine-gold font-cinzel">Values & Lifestyle</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="prose prose-invert max-w-none text-white/90 leading-relaxed">
                      {structuredData.values_lifestyle 
                        ? renderHTML(structuredData.values_lifestyle)
                        : <p className="whitespace-pre-wrap">{legacySections['VALUES & LIFESTYLE'] || 'Analysis not available'}</p>
                      }
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="physical" className="mt-6">
                <Card className="bg-divine-charcoal border-divine-gold/20">
                  <CardHeader>
                    <CardTitle className="text-divine-gold font-cinzel">Physical Attraction Match</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="prose prose-invert max-w-none text-white/90 leading-relaxed">
                      {structuredData.physical_attraction_match 
                        ? renderHTML(structuredData.physical_attraction_match)
                        : <p className="whitespace-pre-wrap">{legacySections['PHYSICAL ATTRACTION'] || 'Analysis not available'}</p>
                      }
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="romantic" className="mt-6">
                <Card className="bg-divine-charcoal border-divine-gold/20">
                  <CardHeader>
                    <CardTitle className="text-divine-gold font-cinzel">Romantic Compatibility</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="prose prose-invert max-w-none text-white/90 leading-relaxed">
                      {structuredData.romantic_compatibility 
                        ? renderHTML(structuredData.romantic_compatibility)
                        : <p className="whitespace-pre-wrap">{legacySections['ROMANTIC COMPATIBILITY'] || 'Analysis not available'}</p>
                      }
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="practical" className="mt-6">
                <Card className="bg-divine-charcoal border-divine-gold/20">
                  <CardHeader>
                    <CardTitle className="text-divine-gold font-cinzel">Practical Alignment</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="prose prose-invert max-w-none text-white/90 leading-relaxed">
                      {structuredData.practical_alignment 
                        ? renderHTML(structuredData.practical_alignment)
                        : <p className="whitespace-pre-wrap">{legacySections['PRACTICAL ALIGNMENT'] || 'Analysis not available'}</p>
                      }
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="astro" className="mt-6">
                <Card className="bg-divine-charcoal border-divine-gold/20">
                  <CardHeader>
                    <CardTitle className="text-divine-gold font-cinzel">Astrological Insights</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {/* Western Zodiac - LLM Analysis */}
                      <div className="glass-gold rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-white/80 font-medium">Western Zodiac</span>
                          <span className="text-divine-gold font-bold">
                            {structuredData.western_zodiac?.score || analysis.zodiac_compatibility?.western?.score || '?'}/100
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-white/60 mb-2">
                          <span>{userPersona?.name || user?.name}: <span className="text-purple-400">{analysis.zodiac_compatibility?.western?.user_sign || 'Unknown'}</span></span>
                          <span>•</span>
                          <span>{match?.name}: <span className="text-pink-400">{analysis.zodiac_compatibility?.western?.match_sign || 'Unknown'}</span></span>
                        </div>
                        <div className="prose prose-invert max-w-none text-white/70 text-sm">
                          {structuredData.western_zodiac?.analysis 
                            ? renderHTML(structuredData.western_zodiac.analysis)
                            : <p>{analysis.zodiac_compatibility?.western?.description || 'Analysis not available'}</p>
                          }
                        </div>
                      </div>
                      
                      {/* Chinese Zodiac - LLM Analysis */}
                      <div className="glass-gold rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-white/80 font-medium">Chinese Zodiac</span>
                          <span className="text-divine-gold font-bold">
                            {structuredData.chinese_zodiac?.score || analysis.zodiac_compatibility?.chinese?.score || '?'}/100
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-white/60 mb-2">
                          <span>{userPersona?.name || user?.name}: <span className="text-purple-400">{analysis.zodiac_compatibility?.chinese?.user_sign || 'Unknown'}</span></span>
                          <span>•</span>
                          <span>{match?.name}: <span className="text-pink-400">{analysis.zodiac_compatibility?.chinese?.match_sign || 'Unknown'}</span></span>
                        </div>
                        <div className="prose prose-invert max-w-none text-white/70 text-sm">
                          {structuredData.chinese_zodiac?.analysis 
                            ? renderHTML(structuredData.chinese_zodiac.analysis)
                            : <p>{analysis.zodiac_compatibility?.chinese?.description || 'Analysis not available'}</p>
                          }
                        </div>
                      </div>
                      
                      {/* Combined Score */}
                      {(structuredData.western_zodiac?.score || structuredData.chinese_zodiac?.score || analysis.zodiac_compatibility?.combined_score) && (
                        <div className="glass-gold rounded-lg p-4 border border-divine-gold/30">
                          <div className="flex items-center justify-between">
                            <span className="text-divine-gold font-medium">Combined Astrological Score</span>
                            <span className="text-2xl font-bold text-divine-gold">
                              {Math.round(((structuredData.western_zodiac?.score || analysis.zodiac_compatibility?.western?.score || 50) + 
                                (structuredData.chinese_zodiac?.score || analysis.zodiac_compatibility?.chinese?.score || 50)) / 2)}/100
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="strengths" className="mt-6">
                <div className="grid gap-6 md:grid-cols-2">
                  {/* Strengths */}
                  <Card className="bg-divine-charcoal border-divine-gold/20">
                    <CardHeader>
                      <CardTitle className="text-emerald-400 font-cinzel flex items-center gap-2">
                        <TrendingUp className="w-5 h-5" />
                        Relationship Strengths
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {structuredData.strengths && Array.isArray(structuredData.strengths) 
                        ? renderList(structuredData.strengths, <Star className="w-4 h-4 text-emerald-400" />)
                        : <p className="text-white/90 whitespace-pre-wrap">{legacySections['STRENGTHS'] || 'Analysis not available'}</p>
                      }
                    </CardContent>
                  </Card>
                  
                  {/* Challenges */}
                  <Card className="bg-divine-charcoal border-divine-gold/20">
                    <CardHeader>
                      <CardTitle className="text-amber-400 font-cinzel flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5" />
                        Potential Challenges
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {structuredData.challenges && Array.isArray(structuredData.challenges) 
                        ? renderList(structuredData.challenges, <AlertTriangle className="w-4 h-4 text-amber-400" />)
                        : <p className="text-white/90 whitespace-pre-wrap">{legacySections['CHALLENGES'] || 'Analysis not available'}</p>
                      }
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              <TabsContent value="challenges" className="mt-6">
                <Card className="bg-divine-charcoal border-divine-gold/20">
                  <CardHeader>
                    <CardTitle className="text-cyan-400 font-cinzel flex items-center gap-2">
                      <Lightbulb className="w-5 h-5" />
                      Recommendations & Tips
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {structuredData.recommendations && Array.isArray(structuredData.recommendations) 
                      ? renderList(structuredData.recommendations, <Lightbulb className="w-4 h-4 text-cyan-400" />)
                      : <p className="text-white/90 whitespace-pre-wrap">{legacySections['RECOMMENDATIONS'] || 'Analysis not available'}</p>
                    }
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
            
            {/* Mobile Bottom Tabs - Larger version */}
            <div className="md:hidden mt-8 pb-4">
              <div className="grid grid-cols-4 gap-2">
                {tabItems.map((item) => (
                  <button
                    key={item.value}
                    onClick={() => setActiveTab(item.value)}
                    className={`py-3 px-2 rounded-lg text-sm font-medium transition-all ${
                      activeTab === item.value
                        ? "bg-divine-gold text-black"
                        : "bg-divine-charcoal border border-divine-gold/20 text-white/70 hover:bg-white/5 hover:text-white"
                    }`}
                    data-testid={`mobile-bottom-tab-${item.value}`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default CompatibilityReport;
